import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Flex, Table, TableProps } from 'antd';

// components
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import StatusComponent from 'components/status/status';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// services

// styles

// models
import { HeirsTransferListItem } from '../models/heir';
import { onGetHeirsTransferList } from '../services/heirsService';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface HeirsTransactionsListPageProps {}

const HeirsTransactionsListPage: FunctionComponent<HeirsTransactionsListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<HeirsTransferListItem[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<HeirsTransferListItem>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => `#${code}`,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status, record) => (
				<StatusComponent text={isRTL() ? record.translated_status.ar : record.translated_status.en} color={StatusColors[status]} />
			),
		},
		{
			title: t('ACTIONS.NAME_OF_DECEASED'),
			dataIndex: 'deceased_name',
			key: 'deceased_name',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getHeirsTransactionsList();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('heirloom_transaction');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getHeirsTransactionsList = async () => {
		try {
			setIsLoading(true);

			const response = await onGetHeirsTransferList(getCurrentPage());

			setRequests(response.heirloom_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetHeirsTransferList(getCurrentPage());
			setRequests(response.heirloom_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.HEIRS_TRANSFER')}
				subTitle={t('ACTIONS.DECEASED_EQUITY')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default HeirsTransactionsListPage;
